import React, { useState } from 'react';
import { FiX, FiLoader } from 'react-icons/fi';

import { useBlocklistCategories } from '@/features/onboarding/hooks/useBlocklist';
import type { BlocklistProperty, BlocklistCategory } from '@/features/onboarding/types';

export interface AddToBlocklistData {
  property: BlocklistProperty;
  valueProperty: string;
  category: BlocklistCategory;
  reason: string;
}

interface AddToBlocklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: AddToBlocklistData) => void;
  isLoading?: boolean;
  error?: string | null;
}

/**
 * Apply mask to CPF (000.000.000-00)
 */
const applyCPFMask = (value: string): string => {
  const numbers = value.replace(/\D/g, '').slice(0, 11);
  return numbers
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

/**
 * Apply mask to CNPJ (00.000.000/0000-00)
 */
const applyCNPJMask = (value: string): string => {
  const numbers = value.replace(/\D/g, '').slice(0, 14);
  return numbers
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

/**
 * Apply mask to phone/celular ((00) 00000-0000 or (00) 0000-0000)
 */
const applyPhoneMask = (value: string): string => {
  const numbers = value.replace(/\D/g, '').slice(0, 11);
  if (numbers.length <= 10) {
    // Telefone fixo: (00) 0000-0000
    return numbers.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  // Celular: (00) 00000-0000
  return numbers.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})$/, '$1-$2');
};

/**
 * Remove mask from value (keep only numbers or original for email)
 */
const removeMask = (value: string, property: BlocklistProperty): string => {
  if (property === 'email') return value;
  return value.replace(/\D/g, '');
};

export const AddToBlocklistModal: React.FC<AddToBlocklistModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  error = null,
}) => {
  const [property, setProperty] = useState<BlocklistProperty | ''>('');
  const [valueProperty, setValueProperty] = useState('');
  const [category, setCategory] = useState<BlocklistCategory>('');
  const [reason, setReason] = useState('');

  const { data: categories, isLoading: categoriesLoading } = useBlocklistCategories();

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (property && valueProperty && category && reason) {
      // Remove mask before sending to API
      const cleanValue = removeMask(valueProperty, property);
      onConfirm({ property, valueProperty: cleanValue, category, reason });
    }
  };

  const resetForm = () => {
    setProperty('');
    setValueProperty('');
    setCategory('');
    setReason('');
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    switch (property) {
      case 'cpf':
        setValueProperty(applyCPFMask(inputValue));
        break;
      case 'cnpj':
        setValueProperty(applyCNPJMask(inputValue));
        break;
      case 'celular':
      case 'telefone':
        setValueProperty(applyPhoneMask(inputValue));
        break;
      case 'email':
      default:
        setValueProperty(inputValue);
        break;
    }
  };

  const getValuePlaceholder = () => {
    switch (property) {
      case 'cpf':
        return '000.000.000-00';
      case 'cnpj':
        return '00.000.000/0000-00';
      case 'email':
        return 'exemplo@email.com';
      case 'celular':
        return '(00) 00000-0000';
      case 'telefone':
        return '(00) 0000-0000';
      default:
        return 'Selecione um campo primeiro';
    }
  };

  const isFormValid = property && valueProperty && category && reason;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        {/* Header with Icon */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-red-600 rounded-full relative">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-0.5 bg-red-600 rotate-45" />
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="p-1 text-neutral-500 hover:text-neutral-700 transition-colors disabled:opacity-50"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-neutral-800 mb-2">Adicionar ao Blacklist</h2>
        <p className="text-sm text-neutral-600 mb-6">
          Forneça os detalhes para o envio ao Blacklist da conta da empresa Fiducia SCM.
        </p>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Form */}
        <div className="space-y-4 mb-6">
          {/* Field Selection */}
          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Campos para bloqueio <span className="text-red-600">*</span>
            </label>
            <select
              value={property}
              onChange={(e) => {
                setProperty(e.target.value as BlocklistProperty);
                setValueProperty('');
              }}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm appearance-none bg-white disabled:bg-neutral-100"
            >
              <option value="">Selecionar</option>
              <option value="cnpj">CNPJ</option>
              <option value="cpf">CPF</option>
              <option value="email">Email</option>
              <option value="celular">Celular</option>
              <option value="telefone">Telefone</option>
            </select>
          </div>

          {/* Value Input */}
          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Valor <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={valueProperty}
              onChange={handleValueChange}
              placeholder={getValuePlaceholder()}
              disabled={isLoading || !property}
              maxLength={150}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm disabled:bg-neutral-100"
            />
          </div>

          {/* Category Selection */}
          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Categoria do motivo <span className="text-red-600">*</span>
            </label>
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
              }}
              disabled={isLoading || categoriesLoading}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm appearance-none bg-white disabled:bg-neutral-100"
            >
              <option value="">{categoriesLoading ? 'Carregando...' : 'Selecionar'}</option>
              {categories?.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Reason Textarea */}
          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Detalhes do motivo <span className="text-red-600">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder="Inserir"
              rows={6}
              disabled={isLoading}
              maxLength={500}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm resize-none disabled:bg-neutral-100"
            />
            <span className="text-xs text-neutral-500 mt-1 block text-right">
              {reason.length}/500 caracteres
            </span>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="px-6 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-lg font-medium hover:bg-neutral-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isFormValid || isLoading}
            className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && <FiLoader className="w-4 h-4 animate-spin" />}
            {isLoading ? 'Adicionando...' : 'Confirmar Blacklist'}
          </button>
        </div>
      </div>
    </div>
  );
};
